import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Icon } from "@/lib/icons";
import { Markdown } from "@/lib/markdown";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, Lock, Crown, Loader2, CheckCircle2, BookmarkPlus, Sparkles } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const LEVELS = ["Beginner", "Intermediate", "Advanced", "Expert"];

export default function KnowledgeCategory() {
  const { categoryId } = useParams();
  const { user } = useAuth();
  const isPro = user?.is_pro || user?.tier === "pro";
  const [cat, setCat] = useState(null);
  const [level, setLevel] = useState("Beginner");
  const [open, setOpen] = useState(false);
  const [lesson, setLesson] = useState(null);
  const [loading, setLoading] = useState(false);
  const [completed, setCompleted] = useState(false);

  useEffect(() => { api.get(`/knowledge/category/${categoryId}`).then((r) => setCat(r.data)).catch(() => {}); }, [categoryId]);

  const locked = (lvl) => (lvl === "Advanced" || lvl === "Expert") && !isPro;

  const openLesson = async (topic) => {
    if (locked(level)) { toast.error("Advanced & Expert lessons require ECO Pro."); return; }
    setOpen(true); setLoading(true); setLesson(null); setCompleted(false);
    try {
      const { data } = await api.post("/knowledge/lesson", { category_id: categoryId, level, topic });
      setLesson(data);
    } catch (err) {
      if (err.response?.status === 402) { toast.error("Advanced & Expert lessons require ECO Pro."); setOpen(false); }
      else toast.error("Could not load lesson");
    } finally { setLoading(false); }
  };

  const markComplete = async () => {
    await api.post("/progress/complete-lesson", { category_id: categoryId, level, topic: lesson.topic });
    setCompleted(true); toast.success("Lesson marked complete! 🎉");
  };

  const save = async () => {
    const { data } = await api.post("/saved", { type: "lesson", ref_id: lesson.key, title: lesson.topic, meta: { category: cat.name, level } });
    toast.success(data.saved ? "Saved to your library" : "Removed from library");
  };

  if (!cat) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-slate-600" /></div>;

  return (
    <div className="space-y-8" data-testid="knowledge-category-page">
      <Link to="/knowledge" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white"><ArrowLeft size={16} /> Knowledge Universe</Link>

      <div className="flex items-start gap-4">
        <div className="h-14 w-14 rounded-2xl flex items-center justify-center shrink-0" style={{ background: `${cat.color}1a`, border: `1px solid ${cat.color}33` }}>
          <Icon name={cat.icon} size={26} style={{ color: cat.color }} strokeWidth={1.5} />
        </div>
        <div>
          <h1 className="font-display text-3xl font-bold text-white tracking-tight">{cat.name}</h1>
          <p className="text-slate-400 mt-1.5 max-w-2xl">{cat.description}</p>
        </div>
      </div>

      {/* level tabs */}
      <div className="flex flex-wrap gap-2">
        {LEVELS.map((lvl) => (
          <button key={lvl} data-testid={`level-${lvl}`} onClick={() => setLevel(lvl)}
            className={`text-sm font-medium px-4 py-2 rounded-full border transition-all flex items-center gap-1.5 ${level === lvl ? "bg-white text-black border-white" : "border-white/10 text-slate-300 hover:bg-white/5"}`}>
            {lvl} {locked(lvl) && <Crown size={12} className="text-amber-400" />}
          </button>
        ))}
      </div>

      {/* topics */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {cat.levels[level].map((topic, i) => (
          <button key={topic} data-testid={`topic-${i}`} onClick={() => openLesson(topic)}
            className="eco-card eco-card-hover p-5 text-left flex items-center gap-3 group">
            <div className="h-9 w-9 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center shrink-0">
              {locked(level) ? <Lock size={16} className="text-amber-400" /> : <Sparkles size={16} className="text-[#4d8bff]" />}
            </div>
            <span className="text-white font-medium flex-1">{topic}</span>
            <span className="font-mono-eco text-[10px] uppercase tracking-wider text-slate-600">{level}</span>
          </button>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-3xl max-h-[88vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-xl">{lesson?.topic || "Loading lesson…"}</DialogTitle></DialogHeader>
          {loading ? (
            <div className="flex flex-col items-center py-16 gap-3">
              <Loader2 className="animate-spin text-[#4d8bff]" />
              <p className="text-sm text-slate-500">ECO is writing your lesson…</p>
            </div>
          ) : lesson ? (
            <>
              <Markdown>{lesson.content}</Markdown>
              <div className="flex gap-3 pt-2 sticky bottom-0">
                <button data-testid="lesson-complete-btn" onClick={markComplete} disabled={completed} className="btn-accent text-sm">
                  <CheckCircle2 size={16} /> {completed ? "Completed" : "Mark complete"}
                </button>
                <button data-testid="lesson-save-btn" onClick={save} className="btn-secondary text-sm"><BookmarkPlus size={16} /> Save</button>
              </div>
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
